import { CustomDialog } from '../../../Component/Dialog';
import { Button, CircularProgress, Grid, Tooltip } from '@mui/material';
import { format } from '../../../../utils/Date';
import useProjectStore from '../../../ProjectCanvas/useProjectStore';
import { ArrowForward, AutoFixHigh, Edit } from '@mui/icons-material';
import WorkflowPublishComment from '../WorkflowPublishComment';
import React, { useState, useEffect } from 'react';
import { send_request } from '../../../../utils/Request';
import './WorkflowPublishConfirmDialog.css';

const DESCRIPTION_OPTIONS = {
  MANUAL: 'manual',
  AI: 'ai',
};

export const WorkflowPublishConfirmDialog = props => {
  const { lastModified } = props.workflow;
  const lastUpdatedText = lastModified
    ? format(lastModified, useProjectStore.getState().timezone, 'relative')
    : 'none';

  const [descriptionOption, setDescriptionOption] = useState(DESCRIPTION_OPTIONS.MANUAL);
  const [manualDescription, setManualDescription] = useState('');
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (props.open) {
      setDescriptionOption(DESCRIPTION_OPTIONS.MANUAL);
      setManualDescription(props.workflow?.description || '');
      setAiGeneratedDescription('');
      setIsGeneratingAi(false);
    }
  }, [props.open]);

  const handleGenerateAiDescription = async () => {
    setIsGeneratingAi(true);
    try {
      const response = await send_request(
        'ai-service/ai/project/generate-summary',
        null,
        { projectId: props.projectId },
        'post'
      );
      const generatedDescription = response?.data?.explanation;
      if (generatedDescription) {
        setAiGeneratedDescription(generatedDescription);
      }
    } catch (err) {
      console.error('Failed to generate AI description:', err);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const getDescriptionToPublish = () => {
    switch (descriptionOption) {
      case DESCRIPTION_OPTIONS.AI:
        return aiGeneratedDescription || props.workflow?.description;
      case DESCRIPTION_OPTIONS.MANUAL:
      default:
        return manualDescription;
    }
  };

  const handlePublishClick = () => {
    const description = getDescriptionToPublish();
    props.onClickPublishButton(description);
  };

  const renderDescriptionContent = () => {
    switch (descriptionOption) {
      case DESCRIPTION_OPTIONS.MANUAL:
        return (
          <textarea
            className="publish-description-textarea"
            value={manualDescription}
            onChange={e => setManualDescription(e.target.value)}
            placeholder="Enter a description for this workflow..."
            maxLength={1000}
          />
        );

      case DESCRIPTION_OPTIONS.AI:
        return (
          <div className="publish-description-ai-container">
            {isGeneratingAi ? (
              <div className="publish-description-skeleton">
                <div className="publish-description-skeleton-line" />
                <div className="publish-description-skeleton-line" />
                <div className="publish-description-skeleton-line" />
              </div>
            ) : aiGeneratedDescription ? (
              <div className="publish-description-ai-preview">{aiGeneratedDescription}</div>
            ) : (
              <div className="publish-description-ai-preview placeholder">
                Click generate to create an AI-powered description
              </div>
            )}
            <button
              className="publish-description-generate-btn"
              onClick={handleGenerateAiDescription}
              disabled={isGeneratingAi}
            >
              {isGeneratingAi ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
              {isGeneratingAi
                ? 'Generating...'
                : aiGeneratedDescription
                ? 'Regenerate'
                : 'Generate with AI'}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <CustomDialog
      isOpen={props.open}
      title={`Publish version ${props.draftVersion}`}
      zIndex={true}
      contents={
        <Grid container item xs={12} direction={'column'}>
          <Grid container item xs={12} direction={'row'} alignItems={'center'} className={'frame'}>
            <Grid xs={5} container itemm direction={'column'}>
              <p className={'bold'}>Draft</p>
              <p className={'rmv-padding'}>Version {props.draftVersion}</p>
              <Tooltip
                title={
                  props.workflow.lastModified
                    ? format(props.workflow.lastModified, useProjectStore.getState().timezone)
                    : 'none'
                }
              >
                <div>
                  <p className={'s-text'}>{`Last updated  ${lastUpdatedText}`}</p>
                </div>
              </Tooltip>
            </Grid>
            <Grid item xs={2} className={'arrowIcon'}>
              <ArrowForward />
            </Grid>
            <Grid container item xs={5} direction={'column'}>
              <p className={'bold'}>Production</p>
              <p className={'rmv-padding'}>Version {props.contents}</p>

              <Tooltip
                title={
                  props.lastPublished
                    ? format(props.lastPublished, useProjectStore.getState().timezone)
                    : 'none'
                }
              >
                <div>
                  <p className={'s-text'}>
                    {`Published  ${
                      props.lastPublished
                        ? format(props.lastPublished, useProjectStore.getState().timezone, 'relative')
                        : 'none'
                    }`}
                  </p>
                </div>
              </Tooltip>
            </Grid>
          </Grid>

          {!props.isPublishing && (
            <>
              <div className="publish-description-section">
                <p className="publish-description-label">Workflow Description</p>
                <div className="publish-description-options">
                  <div
                    className={`publish-description-option ${
                      descriptionOption === DESCRIPTION_OPTIONS.MANUAL ? 'selected' : ''
                    }`}
                    onClick={() => setDescriptionOption(DESCRIPTION_OPTIONS.MANUAL)}
                  >
                    <Edit />
                    Edit manually
                  </div>
                  <div
                    className={`publish-description-option ${
                      descriptionOption === DESCRIPTION_OPTIONS.AI ? 'selected' : ''
                    }`}
                    onClick={() => setDescriptionOption(DESCRIPTION_OPTIONS.AI)}
                  >
                    <AutoFixHigh />
                    Use AI
                  </div>
                </div>
                <div className="publish-description-content">{renderDescriptionContent()}</div>
              </div>

              <WorkflowPublishComment setPublishComment={props.publishComment} />
            </>
          )}
        </Grid>
      }
      buttons={
        <>
          <Button
            onClick={props.onClickCancelButton}
            disabled={props.isPublishing}
            variant={'outlined'}
            id="publish-dialog-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublishClick}
            variant={'contained'}
            color={'success'}
            disabled={
              props.isPublishing || (descriptionOption === DESCRIPTION_OPTIONS.AI && isGeneratingAi)
            }
            id="publish-dialog-publish-btn"
          >
            Publish
            {props.isPublishing && <CircularProgress size={24} className="buttonProgress" />}
          </Button>
        </>
      }
    />
  );
};
